/**
 * Intrinsic pixel dimensions of every bundled image, generated from the files
 * themselves.
 *
 * Giving each <img> its real width/height lets the browser reserve the correct
 * box before the bytes arrive, which keeps Cumulative Layout Shift at zero.
 * Layout is still driven entirely by CSS — these attributes only supply the
 * aspect ratio, so nothing about the rendered design changes.
 */
export const imageDimensions: Record<string, { width: number; height: number }> = {
  "/apple-touch-icon.png": { width: 180, height: 180 },
  "/assets/images/blog-bathroom-remodel-planning.webp": { width: 1000, height: 938 },
  "/assets/images/blog-deck-maintenance.webp": { width: 1000, height: 750 },
  "/assets/images/blog-drywall-repair-signs.webp": { width: 1000, height: 670 },
  "/assets/images/blog-electrical-safety.webp": { width: 1000, height: 750 },
  "/assets/images/blog-handyman-vs-contractor.webp": { width: 1000, height: 780 },
  "/assets/images/blog-interior-paint-colors.webp": { width: 1000, height: 874 },
  "/assets/images/blog-plumbing-problems.webp": { width: 1000, height: 834 },
  "/assets/images/hero-about.webp": { width: 1440, height: 1279 },
  "/assets/images/hero-blog.webp": { width: 1440, height: 1080 },
  "/assets/images/hero-gallery.webp": { width: 1440, height: 971 },
  "/assets/images/hero-home.webp": { width: 1440, height: 1132 },
  "/assets/images/hero-services.webp": { width: 1440, height: 1063 },
  "/assets/images/service-bathroom-remodeling.webp": { width: 1440, height: 1351 },
  "/assets/images/service-ceiling-fan-installation.webp": { width: 1440, height: 960 },
  "/assets/images/service-deck-installation.webp": { width: 1440, height: 1066 },
  "/assets/images/service-door-installation.webp": { width: 1440, height: 1080 },
  "/assets/images/service-drywall-repair.webp": { width: 1440, height: 971 },
  "/assets/images/service-electrical.webp": { width: 1440, height: 1054 },
  "/assets/images/service-garbage-disposal-replacement.webp": { width: 1024, height: 640 },
  "/assets/images/service-interior-exterior-painting.webp": { width: 1440, height: 1259 },
  "/assets/images/service-molding-trim-installation.webp": { width: 1440, height: 960 },
  "/assets/images/service-plumbing.webp": { width: 1440, height: 1080 },
  "/assets/images/service-shower-door-installation.webp": { width: 1440, height: 1080 },
  "/assets/images/service-tv-mounting.webp": { width: 1440, height: 1080 },
  "/assets/images/why-choose-us.webp": { width: 1000, height: 738 },
  "/favicon.png": { width: 64, height: 64 },
  "/logo.png": { width: 602, height: 414 },
  "/og-image.png": { width: 1200, height: 630 },
};

/**
 * Spread onto an <img> to supply its intrinsic size:
 *   <img src={src} alt={alt} {...imgSize(src)} />
 * Returns nothing for an unknown path, so callers never render broken markup.
 */
export const imgSize = (src: string): { width?: number; height?: number } =>
  imageDimensions[src] ?? {};
